import {ChangeDetectionStrategy, Component, inject, InjectionToken} from '@angular/core';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {
    AppValidators,
    EMAIL_MAX_LENGTH,
    COUNTRY_CALLING_CODE_OPTIONS,
    emptyToNull,
    formatPhoneNumber,
    getPhoneNumberDetails,
    TranslatePipe,
} from '@brunata-angular/util';
import {
    ControlMessagesComponent,
    InputDropdownGroupComponent,
    InputTextGroupComponent,
    ModalCloser,
} from '@brunata-angular/ui';
import {LocationsConsumer} from '@shared/services/location.service';
import {ConsumerUpdate} from '../consumer.service';

@Component({
    selector: 'app-edit-consumer-modal',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        InputTextGroupComponent,
        InputDropdownGroupComponent,
        ControlMessagesComponent,
    ],
    templateUrl: './edit-consumer-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditConsumerModalComponent {
    static readonly inputToken = new InjectionToken<LocationsConsumer>(
        'EditConsumerModalComponent.input',
    );

    static readonly resultToken = new InjectionToken<ModalCloser<ConsumerUpdate>>(
        'EditConsumerModalComponent.result',
    );

    protected readonly emailMaxLength = EMAIL_MAX_LENGTH;

    private readonly consumer = inject(EditConsumerModalComponent.inputToken);
    private readonly closer = inject(EditConsumerModalComponent.resultToken);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    protected readonly countryCallingCodes = COUNTRY_CALLING_CODE_OPTIONS;

    private readonly phoneNumberDetails = getPhoneNumberDetails(this.consumer.phoneNumber ?? '');

    protected readonly formGroup = this.formBuilder.group(
        {
            name: [this.consumer.name ?? '', [Validators.required, Validators.maxLength(100)]],
            consumerNo: [this.consumer.consumerNo ?? '', Validators.maxLength(20)],
            email: [
                this.consumer.email ?? '',
                [AppValidators.email, Validators.maxLength(EMAIL_MAX_LENGTH)],
            ],
            countryCallingCode: [this.phoneNumberDetails.countryCallingCode],
            localNumber: [this.phoneNumberDetails.localNumber],
        },
        {validators: AppValidators.phoneNumber(formatPhoneNumber)},
    );

    protected submit(): void {
        this.formGroup.markAllAsTouched();
        if (this.formGroup.invalid) {
            return;
        }

        const value = this.formGroup.getRawValue();
        this.closer.close({
            name: value.name.trim(),
            // Empty rather than null: the endpoint reads null as "not sent" and keeps the old one.
            consumerNo: value.consumerNo.trim(),
            email: emptyToNull(value.email),
            phoneNumber: this.buildPhoneNumber(),
            isPhoneMobile: this.consumer.isPhoneMobile ?? null,
        });
    }

    protected dismiss(): void {
        this.closer.dismiss('cancel');
    }

    private buildPhoneNumber(): string | null {
        const {countryCallingCode, localNumber} = this.formGroup.controls;
        // A country calling code we don't list parses as no code at all, and reformatting would
        // drop the '+'. Only rebuild the number when the user actually edited one of the halves.
        if (!countryCallingCode.dirty && !localNumber.dirty) {
            return this.consumer.phoneNumber ?? null;
        }
        return emptyToNull(formatPhoneNumber(countryCallingCode.value, localNumber.value));
    }
}
